import { useEffect, useState } from "react";

import { initialize } from "launchdarkly-js-client-sdk";
import type { LDClient } from "launchdarkly-js-client-sdk";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import noop from "lodash/noop";

import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { LaunchDarklyConfig } from "@/crossPlatform/LaunchDarkly/config";
import { createInitialMultiContext } from "@/crossPlatform/LaunchDarkly/context";
import type {
  initializeLaunchDarkly as initializeType,
  LaunchDarkly,
  LaunchDarklyDeviceContext,
  LaunchDarklyUserMultiContext,
  LDWrapperType,
} from "@/crossPlatform/LaunchDarkly/types";
import { handleLaunchDarklyError } from "@/utils/launchDarkly.utils";

// Create LaunchDarkly client instance with anonymous context
// This client is initialized at module load and later identified with device context
const LdClientInstance: LDClient = initialize(
  ProcessEnvironment.LaunchDarklyWebClientSideId,
  {
    key: LaunchDarklyConfig.Key,
    kind: LaunchDarklyConfig.Kind,
  },
  {
    // Explicitly configure event flush interval (default: 2000ms)
    flushInterval: 2000,
    logger: {
      // required console.debug to be defined
      debug: noop,
      error: handleLaunchDarklyError,
      // required console.info to be defined
      info: noop,
      // required console.warn to be defined
      warn: noop,
    },
    // Explicitly enable streaming to match mobile's always-on streaming behavior
    // eslint-disable-next-line @typescript-eslint/naming-convention -- this key is defined by the LaunchDarkly package
    streaming: true,
    // Explicitly configure stream reconnect delay (default: 1000ms)
    streamReconnectDelay: 1000,
  },
);

const initializeLaunchDarkly: initializeType = async (
  deviceContext: LaunchDarklyDeviceContext,
) => {
  const context = createInitialMultiContext(deviceContext);

  await LdClientInstance.identify(context).catch((caughtErr: Error) => {
    // logging errors is already handled via the LDOptions.logger defined for "error" level,
    // which also handles invoking captureException for datadog;
    // so here we simply console.log the message, and continue onwards

    // eslint-disable-next-line no-console
    console.log(caughtErr);
  });
};

const LDWrapper: LDWrapperType = withLDProvider({
  clientSideID: ProcessEnvironment.LaunchDarklyWebClientSideId,
  ldClient: LdClientInstance,
  reactOptions: {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- this key is defined by the LaunchDarkly package
    useCamelCaseFlagKeys: false,
  },
});

// Generic hook that subscribes to flag changes and triggers re-renders
// eslint-disable-next-line max-params
const useVariation = <T>(key: string, fallback: T): T => {
  const [value, setValue] = useState<T>(() =>
    LdClientInstance.variation(key, fallback),
  );

  useEffect(() => {
    const handler = (changes: Record<string, { current: unknown }>) => {
      if (!(key in changes)) return;

      const newValue = changes[key].current;
      // Apply fallback for null/undefined to match initial variation() behavior
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setValue((newValue ?? fallback) as T);
    };
    LdClientInstance.on("change", handler);
    return () => LdClientInstance.off("change", handler);
  }, [key, fallback]);

  return value;
};

// Web variation hooks with reactivity to match native behavior
// eslint-disable-next-line max-params
const useStringVariation = (key: string, fallback: string): string =>
  useVariation(key, fallback);

// eslint-disable-next-line max-params, @typescript-eslint/naming-convention
const useBoolVariation = (key: string, fallback: boolean): boolean =>
  useVariation(key, fallback);

// eslint-disable-next-line max-params
const useTypedVariation = <T = unknown>(key: string, fallback: T): T =>
  useVariation(key, fallback);

const useLaunchDarkly = (): LaunchDarkly => ({
  LDProviders: [],
  useBoolVariation,
  useStringVariation,
  useTypedVariation,
});

// Client wrapper with compatibility for setConnectionMode (no-op on web)
const client = {
  identify: (context: LaunchDarklyUserMultiContext) =>
    LdClientInstance.identify(context),
  setConnectionMode: (_mode: string): Promise<void> => Promise.resolve(),
};

export { client, initializeLaunchDarkly, LDWrapper };
export default useLaunchDarkly;
